import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COUNTRIES = [["NG", "Nigeria"], ["US", "United States"], ["GB", "United Kingdom"]];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { format } = useCurrency();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [code, setCode] = useState("");
  const [form, setForm] = useState({
    email: user?.email || "", name: user?.name || "", phone: "",
    line1: "", city: "", state: "", postal_code: "", country: "NG", shipping_method: "Standard",
  });

  const shipping = subtotal >= 50 ? 0 : 4.99;
  const discountAmt = discount?.discount || 0;
  const total = Math.max(0, subtotal - discountAmt + shipping);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const applyCode = async () => {
    try {
      const { data } = await api.post("/discounts/validate", { code, subtotal });
      setDiscount(data); toast.success(`Code ${data.code} applied`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, variant_id: i.variant_id, quantity: i.quantity })),
        email: form.email,
        shipping_address: {
          name: form.name, line1: form.line1, city: form.city, state: form.state,
          postal_code: form.postal_code, country: form.country, phone: form.phone,
        },
        shipping_method: form.shipping_method,
        discount_code: discount?.code || null,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return <div className="max-w-xl mx-auto px-5 py-28 text-center">
      <h1 className="font-display text-3xl font-bold">Nothing to check out</h1>
      <Button asChild className="rounded-full mt-6"><Link to="/shop">Browse products</Link></Button>
    </div>;
  }

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-12">
      <h1 className="font-display text-4xl font-bold tracking-tight mb-10">Checkout</h1>
      <form onSubmit={placeOrder} className="grid lg:grid-cols-5 gap-12">
        <div className="lg:col-span-3 space-y-8">
          <div>
            <h2 className="font-display text-xl font-bold mb-4">Contact</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Email *</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="co-email" /></div>
              <div><Label>Full name *</Label><Input required value={form.name} onChange={set("name")} data-testid="co-name" /></div>
              <div><Label>Phone *</Label><Input required value={form.phone} onChange={set("phone")} data-testid="co-phone" /></div>
            </div>
            {!user && <p className="text-xs text-zinc-500 mt-3">Checking out as guest. <Link to="/login" className="underline">Sign in</Link> to save your order.</p>}
          </div>
          <div>
            <h2 className="font-display text-xl font-bold mb-4">Shipping Address</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label>Street address *</Label><Input required value={form.line1} onChange={set("line1")} data-testid="co-line1" /></div>
              <div><Label>City *</Label><Input required value={form.city} onChange={set("city")} data-testid="co-city" /></div>
              <div><Label>State / Region</Label><Input value={form.state} onChange={set("state")} data-testid="co-state" /></div>
              <div><Label>Postal code</Label><Input value={form.postal_code} onChange={set("postal_code")} data-testid="co-postal" /></div>
              <div>
                <Label>Country *</Label>
                <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                  <SelectTrigger data-testid="co-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(([k, n]) => <SelectItem key={k} value={k}>{n}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl font-bold mb-4">Shipping Method</h2>
            <Select value={form.shipping_method} onValueChange={(v) => setForm({ ...form, shipping_method: v })}>
              <SelectTrigger data-testid="co-shipping"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Standard">Standard (7-12 days)</SelectItem>
                <SelectItem value="Express">Express (3-6 days)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-zinc-50 rounded-xl p-6 sticky top-28">
            <h2 className="font-display text-xl font-bold mb-5">Your Order</h2>
            <div className="space-y-4 max-h-64 overflow-auto mb-5">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <div className="relative"><img src={i.image} alt="" className="w-14 h-16 object-cover rounded-md" />
                    <span className="absolute -top-2 -right-2 bg-zinc-900 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">{i.quantity}</span></div>
                  <div className="flex-1 text-sm"><p className="font-medium line-clamp-1">{i.title}</p>{i.variant_label && <p className="text-xs text-zinc-500">{i.variant_label}</p>}</div>
                  <span className="text-sm font-medium">{format(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mb-5">
              <Input placeholder="Discount code" value={code} onChange={(e) => setCode(e.target.value)} className="flex-1" data-testid="discount-code" />
              <Button type="button" variant="outline" onClick={applyCode} data-testid="apply-discount"><Tag size={15} /></Button>
            </div>
            <div className="space-y-2 text-sm border-t border-zinc-200 pt-4">
              <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>{format(subtotal)}</span></div>
              {discountAmt > 0 && <div className="flex justify-between text-green-600"><span>Discount ({discount.code})</span><span>-{format(discountAmt)}</span></div>}
              <div className="flex justify-between"><span className="text-zinc-500">Shipping</span><span>{shipping === 0 ? "Free" : format(shipping)}</span></div>
              <div className="border-t border-zinc-200 pt-2 flex justify-between font-semibold text-base"><span>Total</span><span data-testid="co-total">{format(total)}</span></div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12 mt-6" data-testid="place-order-btn">
              <Lock size={15} className="mr-2" />{loading ? "Redirecting..." : "Pay with Stripe"}
            </Button>
            <p className="text-xs text-zinc-400 text-center mt-3">Test card 4242 4242 4242 4242 · any future date · any CVC</p>
          </div>
        </div>
      </form>
    </div>
  );
}
